import { apiService } from '@/shared/services/apiServices';
import { AxiosError } from 'axios';
import { OFFER_ERROR_MESSAGES } from '../errors';

// ============================================================================
// API Response Types
// ============================================================================

export interface UserInfo {
  userId: string;
  name: string;
  profileUrl: string | null;
}

export interface ValidityPeriod {
  startDate: string;
  endDate: string;
}

export interface SubscriptionPlanSummary {
  id: string;
  name: string;
}

export interface Offer {
  id: string;
  offerName: string;
  subscriptionPlans: SubscriptionPlanSummary[];
  discountType: string;
  discountValue: number;
  validityPeriod: ValidityPeriod;
  status: string;
  isExpired?: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: UserInfo | null;
  updatedBy: UserInfo | null;
  discountId?: string;
}

export interface ViewOfferApiResponse {
  success: boolean;
  data: Offer;
}

export interface ViewOfferResult {
  success: boolean;
  data?: Offer;
  error?: string;
}

export interface ViewOfferOptions {
  offerId: string;
}

// ============================================================================
// Error Messages
// ============================================================================

export const ERROR_MESSAGES = {
  DEFAULT_ERROR: 'Wystąpił błąd podczas pobierania danych oferty',
  NETWORK_ERROR: OFFER_ERROR_MESSAGES.NETWORK_ERROR,
  UNEXPECTED_ERROR: OFFER_ERROR_MESSAGES.UNEXPECTED_ERROR,
} as const;

const extractErrorMessage = (errorData: unknown): string => {
  if (!errorData) {
    return ERROR_MESSAGES.DEFAULT_ERROR;
  }

  if (typeof errorData === 'string') {
    return errorData;
  }

  if (typeof errorData === 'object') {
    const apiError = errorData as { message?: string; error?: string };
    const errorMessage = apiError.message || apiError.error;

    if (errorMessage) {
      return errorMessage;
    }

    return ERROR_MESSAGES.DEFAULT_ERROR;
  }

  return ERROR_MESSAGES.DEFAULT_ERROR;
};

/**
 * Returns true if the validity end date is in the past (today > end date by calendar day).
 * So an offer with endDate "2026-02-19" is expired from 2026-02-20 onward.
 */
function isPastEndDate(endDate: string | undefined): boolean {
  if (!endDate || typeof endDate !== 'string') {
    return false;
  }
  const end = new Date(endDate);
  if (Number.isNaN(end.getTime())) {
    return false;
  }
  const today = new Date();
  end.setUTCHours(0, 0, 0, 0);
  today.setUTCHours(0, 0, 0, 0);
  return today.getTime() > end.getTime();
}

/**
 * Normalize API offer (snake_case or camelCase) to Offer type.
 * API may return is_expired, offer_name, subscription_plans, validity_period, etc.
 * When isExpired is not sent, it is derived from validityPeriod.endDate (past = expired).
 */
function normalizeOfferData(raw: Record<string, unknown>): Offer {
  const data = raw as unknown as Offer;
  const subscriptionPlans = Array.isArray(raw['subscription_plans'])
    ? (raw['subscription_plans'] as Record<string, unknown>[]).map((p) => ({
        id: String(p['id'] ?? ''),
        name: String(p['name'] ?? ''),
      }))
    : (data.subscriptionPlans ?? []);
  const validityPeriod = raw['validity_period'] as
    | Record<string, unknown>
    | undefined;
  const resolvedValidity =
    data.validityPeriod ??
    (validityPeriod
      ? {
          startDate: String(
            validityPeriod['start_date'] ?? validityPeriod['startDate'] ?? ''
          ),
          endDate: String(
            validityPeriod['end_date'] ?? validityPeriod['endDate'] ?? ''
          ),
        }
      : { startDate: '', endDate: '' });
  const apiIsExpired = raw['is_expired'] === true || raw['isExpired'] === true;
  const isExpired =
    apiIsExpired ||
    (resolvedValidity?.endDate
      ? isPastEndDate(resolvedValidity.endDate)
      : false);
  return {
    ...data,
    offerName: data.offerName ?? String(raw['offer_name'] ?? ''),
    subscriptionPlans: data.subscriptionPlans?.length
      ? data.subscriptionPlans
      : subscriptionPlans,
    validityPeriod: resolvedValidity,
    isExpired,
    discountId:
      data.discountId ?? String(raw['discount_id'] ?? raw['discountId'] ?? ''),
  };
}

/**
 * Fetches offer details by offerId (UUID).
 * Endpoint: GET offer-management/offers/{offerId}
 */
export const viewOffer = async (
  options: ViewOfferOptions
): Promise<ViewOfferResult> => {
  const { offerId } = options;

  if (!offerId?.trim()) {
    return {
      success: false,
      error: ERROR_MESSAGES.DEFAULT_ERROR,
    };
  }

  try {
    const result = await apiService.getAPI(
      `offer-management/offers/${offerId.trim()}`
    );

    if (!result.error) {
      // API returns { success: true, data: {...} } (may be snake_case)
      const response = result.data as ViewOfferApiResponse;
      const rawData = response?.data ?? result.data;

      if (!rawData || typeof rawData !== 'object') {
        return {
          success: false,
          error: ERROR_MESSAGES.DEFAULT_ERROR,
        };
      }

      const offer = normalizeOfferData(
        rawData as unknown as Record<string, unknown>
      );
      return {
        success: true,
        data: offer,
      };
    } else {
      const errorData = result.data;
      const isNetworkError = result.isNetworkError;

      const errorMessage = extractErrorMessage(errorData);

      if (isNetworkError) {
        return {
          success: false,
          error: ERROR_MESSAGES.NETWORK_ERROR,
        };
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  } catch (error) {
    if (error instanceof AxiosError) {
      const errorData = error.response?.data;
      const isNetworkError = !error.response && !!error.request;

      if (isNetworkError) {
        return {
          success: false,
          error: ERROR_MESSAGES.NETWORK_ERROR,
        };
      }

      const errorMessage = extractErrorMessage(errorData);

      return {
        success: false,
        error: errorMessage,
      };
    }

    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : OFFER_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
